"use client";

import { Info } from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";

/**
 * "You ordered 843, this number is an 854" — explained in place.
 *
 * Renders nothing unless a substitution actually happened, so the ~95% of
 * numbers whose area code matches stay uncluttered.
 *
 * ⚠️ The copy deliberately never names the carrier. Vendor names do not appear
 * in agent-facing text anywhere in this product, and "out of stock, closest
 * available number in the same local area" tells them everything they need
 * without exposing who we buy from.
 */
export function AreaCodeSubstitutionNote({
  orderedAreaCode,
  className = "",
}: {
  orderedAreaCode?: number | string | null;
  className?: string;
}) {
  if (!orderedAreaCode) return null;

  return (
    <span className={`inline-flex items-center gap-1 align-middle ${className}`}>
      <span className="text-gray-500 dark:text-gray-400">
        (ordered {orderedAreaCode})
      </span>
      <TooltipProvider>
        <UITooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Why is this number a different area code?"
              className="inline-flex cursor-help items-center align-middle"
            >
              <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-72 text-left text-xs font-normal normal-case leading-snug tracking-normal text-popover-foreground"
          >
            Area code {orderedAreaCode} was out of stock when this number was set
            up, so the closest available number in the same local area was
            provisioned instead. Same price, same coverage — you were billed for{" "}
            {orderedAreaCode}.
          </TooltipContent>
        </UITooltip>
      </TooltipProvider>
    </span>
  );
}
